import React, { useState, useEffect } from "react";
import API from "../services/api";
import { printNote } from "./PrintPDFUtils";

/* ===============================
   MODAL: CREATE NEW SOAP NOTE
   =============================== */
function SOAPNewModal({ isOpen, onClose, onSubmit, clientId, clientName }) {
  if (!isOpen) return null;

  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [sessionType, setSessionType] = useState("Individual");
  const [mood, setMood] = useState("euthymic/normal/typical");
  const [feelingsExpressed, setFeelingsExpressed] = useState("");
  const [sessionFocus, setSessionFocus] = useState("");
  const [interventions, setInterventions] = useState("TA");
  const [additionalTechniques, setAdditionalTechniques] = useState("");
  const [engagementLevel, setEngagementLevel] = useState("Fully Engaged");
  const [clientResponse, setClientResponse] = useState("");
  const [clientProgress, setClientProgress] = useState("Moderate Progress");
  const [planNotes, setPlanNotes] = useState("");

  const handleSave = () => {
    const soapNoteObj = {
      date,
      sessionType,
      subjective: {
        mood,
        feelingsExpressed,
        sessionFocus
      },
      objective: {
        interventions,
        additionalTechniques,
        engagementLevel
      },
      assessment: {
        clientResponse,
        clientProgress
      },
      plan: planNotes
    };
    onSubmit(soapNoteObj);
  };

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>New SOAP Note</h2>
        
        <div style={styles.clientInfo}>
          <strong>Client:</strong> {clientName || "Selected Client"} | <strong>Date:</strong> {date}
          <button 
            onClick={() => setDate(new Date().toISOString().split("T")[0])}
            style={styles.smallButton}
          >
            Set Today
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={styles.dateInput}
          />
        </div>
        
        <div style={styles.formScrollArea}>
          {/* Session Type */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Type of Session:</h3>
            <select
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value)}
              style={styles.select}
            >
              <option value="Individual">Individual</option>
              <option value="Individual w/ Collateral">Individual w/ Collateral</option>
              <option value="Couples">Couples</option>
              <option value="Family">Family</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Subjective Section */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Subjective</h3>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Mood:</h4>
              <select
                value={mood}
                onChange={(e) => setMood(e.target.value)}
                style={styles.select}
              >
                <option value="euthymic/normal/typical">euthymic/normal/typical</option>
                <option value="dysphoric/depressed/down">dysphoric/depressed/down</option>
                <option value="euphoric/elated">euphoric/elated</option>
                <option value="other">other</option>
              </select>
            </div>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Feelings/Behavior:</h4>
              <textarea
                rows="3"
                value={feelingsExpressed}
                onChange={(e) => setFeelingsExpressed(e.target.value)}
                style={styles.textArea}
                placeholder="Document client's expressed feelings and behaviors..."
              />
            </div>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Session Focus:</h4>
              <textarea
                rows="3"
                value={sessionFocus}
                onChange={(e) => setSessionFocus(e.target.value)}
                style={styles.textArea}
                placeholder="Document the main focus of this session..."
              />
            </div>
          </div>

          {/* Objective Section */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Objective</h3>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Interventions Used:</h4>
              <select
                value={interventions}
                onChange={(e) => setInterventions(e.target.value)}
                style={styles.select}
              >
                <option value="TA">TA</option>
                <option value="CBT">CBT</option>
                <option value="MI">MI</option>
                <option value="Solution-Focused">Solution-Focused</option>
                <option value="Parts (IFS)">Parts (IFS)</option>
                <option value="EMDR">EMDR</option>
                <option value="Play Therapy Techniques">Play Therapy</option>
                <option value="Polyvagal Theory">Polyvagal Theory</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Additional Techniques:</h4>
              <textarea
                rows="3"
                value={additionalTechniques}
                onChange={(e) => setAdditionalTechniques(e.target.value)}
                style={styles.textArea}
                placeholder="Document any additional techniques or interventions used..."
              />
            </div>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Engagement Level:</h4>
              <select
                value={engagementLevel}
                onChange={(e) => setEngagementLevel(e.target.value)}
                style={styles.select}
              >
                <option value="Fully Engaged">Fully Engaged</option>
                <option value="Mostly Engaged">Mostly Engaged</option>
                <option value="Somewhat Engaged">Somewhat Engaged</option>
                <option value="Not Engaged">Not Engaged</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Assessment Section */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Assessment</h3>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Client Response:</h4>
              <textarea
                rows="3"
                value={clientResponse}
                onChange={(e) => setClientResponse(e.target.value)}
                style={styles.textArea}
                placeholder="Document client's response to interventions..."
              />
            </div>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Client Progress:</h4>
              <select
                value={clientProgress}
                onChange={(e) => setClientProgress(e.target.value)}
                style={styles.select}
              >
                <option value="Significant Progress">Significant Progress</option>
                <option value="Moderate Progress">Moderate Progress</option>
                <option value="Mild Progress">Mild Progress</option>
                <option value="No Progress">No Progress noted/observed</option>
                <option value="Regression">Regression in Progress</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Plan Section */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Plan</h3>
            
            <div style={styles.fieldGroup}>
              <textarea
                rows="4"
                value={planNotes}
                onChange={(e) => setPlanNotes(e.target.value)}
                style={styles.textArea}
                placeholder="Document the plan for future sessions..."
              />
            </div>
          </div>
        </div>

        <div style={styles.modalButtons}>
          <button onClick={handleSave} style={styles.saveBtn}>
            Save
          </button>
          <button onClick={onClose} style={styles.cancelBtn}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===============================
   MODAL: VIEW/EDIT EXISTING SOAP
   =============================== */
function SOAPViewModal({ isOpen, onClose, note, onUpdate, onSendAsPDF }) {
  if (!isOpen || !note) return null;

  // Parse
  let parsed = {};
  try {
    parsed = JSON.parse(note.notes);
  } catch (err) {
    console.error("Error parsing note:", err);
  }

  const [editMode, setEditMode] = useState(false);

  // Copy fields from `parsed`
  const [date, setDate] = useState(parsed.date || "");
  const [sessionType, setSessionType] = useState(parsed.sessionType || "Individual");
  const [mood, setMood] = useState(parsed.subjective?.mood || "");
  const [feelingsExpressed, setFeelingsExpressed] = useState(parsed.subjective?.feelingsExpressed || "");
  const [sessionFocus, setSessionFocus] = useState(parsed.subjective?.sessionFocus || "");
  const [interventions, setInterventions] = useState(parsed.objective?.interventions || "");
  const [additionalTechniques, setAdditionalTechniques] = useState(parsed.objective?.additionalTechniques || "");
  const [engagementLevel, setEngagementLevel] = useState(parsed.objective?.engagementLevel || "");
  const [clientResponse, setClientResponse] = useState(parsed.assessment?.clientResponse || "");
  const [clientProgress, setClientProgress] = useState(parsed.assessment?.clientProgress || "");
  const [planNotes, setPlanNotes] = useState(parsed.plan || "");
  
  // Store client name from note for printing
  const [clientName, setClientName] = useState("");
  
  // Fetch client name on load
  useEffect(() => {
    if (note && note.client_id) {
      const fetchClientName = async () => {
        try {
          const res = await API.get(`/therapist/clients/${note.client_id}`);
          if (res.data && res.data.client_name) {
            setClientName(res.data.client_name);
          }
        } catch (err) {
          console.error("Error fetching client name:", err);
        }
      };
      
      fetchClientName();
    }
  }, [note]);

  const handleSave = () => {
    const updatedSOAP = {
      date,
      sessionType,
      subjective: {
        mood,
        feelingsExpressed,
        sessionFocus
      },
      objective: {
        interventions,
        additionalTechniques,
        engagementLevel
      },
      assessment: {
        clientResponse,
        clientProgress
      },
      plan: planNotes
    };
    onUpdate(note.id, updatedSOAP);
    setEditMode(false);
  };
  
  // Handle printing
  const handlePrint = () => {
    const noteData = {
      date,
      sessionType,
      subjective: {
        mood,
        feelingsExpressed,
        sessionFocus
      },
      objective: {
        interventions,
        additionalTechniques,
        engagementLevel
      },
      assessment: {
        clientResponse,
        clientProgress
      },
      plan: planNotes
    };
    
    printNote({notes: JSON.stringify(noteData)}, "SOAP Note", clientName);
  };

  // Helper to render form fields based on editMode
  const renderField = (field, setField, fieldType = "text", options = null) => {
    if (editMode) {
      if (fieldType === "textarea") {
        return (
          <textarea
            rows="3"
            value={field}
            onChange={(e) => setField(e.target.value)}
            style={styles.textArea}
          />
        );
      } else if (fieldType === "select" && options) {
        return (
          <select
            value={field}
            onChange={(e) => setField(e.target.value)}
            style={styles.select}
          >
            {options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      } else {
        return (
          <input
            type="text"
            value={field}
            onChange={(e) => setField(e.target.value)}
            style={styles.input}
          />
        );
      }
    } else {
      return <div style={styles.readOnly}>{field}</div>;
    }
  };

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>
          {editMode ? "Edit SOAP Note" : "View SOAP Note"}
        </h2>

        <div style={{ textAlign: "right", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          {!editMode && (
            <>
              <button
                onClick={handlePrint}
                style={{ ...styles.button, backgroundColor: "#6c757d" }}
              >
                Print/PDF
              </button>
              <button
                onClick={() => setEditMode(true)}
                style={{ ...styles.button, backgroundColor: "#ffc107" }}
              >
                Edit
              </button>
            </>
          )}
          {onSendAsPDF && (
            <button onClick={() => onSendAsPDF(note)} style={{ ...styles.button, backgroundColor: "#1D2C5F" }}>
              ✉️ Send as PDF
            </button>
          )}
          <button onClick={onClose} style={styles.cancelBtn}>
            Close
          </button>
        </div>

        <div style={{ color: "#666", fontSize: "0.9rem", marginBottom: "1rem" }}>
          Created: {new Date(note.created_at).toLocaleString()}
        </div>

        <div style={styles.formScrollArea}>
          {/* DATE */}
          <div style={styles.formSection}>
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Date:</label>
              {editMode ? (
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={styles.input}
                />
              ) : (
                <div style={styles.readOnly}>{date}</div>
              )}
            </div>
          </div>
          
          {/* Session Type */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Type of Session:</h3>
            {renderField(
              sessionType, 
              setSessionType, 
              "select", 
              ["Individual", "Individual w/ Collateral", "Couples", "Family", "Other"]
            )}
          </div>

          {/* Subjective Section */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Subjective</h3>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Mood:</h4>
              {renderField(
                mood, 
                setMood, 
                "select", 
                ["euthymic/normal/typical", "dysphoric/depressed/down", "euphoric/elated", "other"]
              )}
            </div>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Feelings/Behavior:</h4>
              {renderField(feelingsExpressed, setFeelingsExpressed, "textarea")}
            </div>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Session Focus:</h4>
              {renderField(sessionFocus, setSessionFocus, "textarea")}
            </div>
          </div>

          {/* Objective Section */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Objective</h3>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Interventions Used:</h4>
              {renderField(
                interventions, 
                setInterventions, 
                "select", 
                ["TA", "CBT", "MI", "Solution-Focused", "Parts (IFS)", "EMDR", "Play Therapy Techniques", "Polyvagal Theory", "Other"]
              )}
            </div>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Additional Techniques:</h4>
              {renderField(additionalTechniques, setAdditionalTechniques, "textarea")}
            </div>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Engagement Level:</h4>
              {renderField(
                engagementLevel, 
                setEngagementLevel, 
                "select", 
                ["Fully Engaged", "Mostly Engaged", "Somewhat Engaged", "Not Engaged", "Other"]
              )}
            </div>
          </div>

          {/* Assessment Section */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Assessment</h3>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Client Response:</h4>
              {renderField(clientResponse, setClientResponse, "textarea")}
            </div>
            
            <div style={styles.fieldGroup}>
              <h4 style={styles.subSectionHeader}>Client Progress:</h4>
              {renderField(
                clientProgress, 
                setClientProgress, 
                "select", 
                ["Significant Progress", "Moderate Progress", "Mild Progress", "No Progress", "Regression", "Other"]
              )}
            </div>
          </div>

          {/* Plan Section */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Plan</h3>
            
            <div style={styles.fieldGroup}>
              {renderField(planNotes, setPlanNotes, "textarea")}
            </div>
          </div>
        </div>

        {editMode && (
          <div style={styles.modalButtons}>
            <button onClick={handleSave} style={styles.saveBtn}>
              Save Changes
            </button>
            <button 
              onClick={() => setEditMode(false)} 
              style={styles.cancelBtn}
            >
              Cancel Edits
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles for the modals
const styles = {
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "1rem"
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: "1rem",
    borderRadius: "6px",
    maxHeight: "90vh",
    overflowY: "auto",
    width: "700px",
    maxWidth: "94vw",
    boxSizing: "border-box",
    position: "relative"
  },
  modalHeader: {
    fontSize: "1.25rem",
    margin: 0,
    marginBottom: "0.5rem"
  },
  clientInfo: {
    marginBottom: "1rem",
    padding: "0.5rem",
    backgroundColor: "#f8f9fa",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "1rem"
  },
  formScrollArea: {
    maxHeight: "60vh",
    overflowY: "auto",
    padding: "0.5rem",
    border: "1px solid #eee",
    borderRadius: "4px"
  },
  formSection: {
    marginBottom: "1.5rem",
    padding: "0.5rem",
    backgroundColor: "#f9f9f9",
    borderRadius: "4px",
    border: "1px solid #eee"
  },
  fieldGroup: {
    marginBottom: "1rem"
  },
  sectionHeader: {
    fontSize: "1rem",
    fontWeight: "bold",
    margin: "0 0 0.75rem 0",
    color: "#333",
    borderBottom: "1px solid #ddd",
    paddingBottom: "0.25rem"
  },
  subSectionHeader: {
    fontSize: "0.9rem",
    fontWeight: "bold",
    margin: "0.75rem 0 0.5rem 0",
    color: "#333"
  },
  textArea: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
    resize: "vertical",
    fontFamily: "inherit",
    fontSize: "0.9rem",
    boxSizing: "border-box"
  },
  select: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
    backgroundColor: "#fff",
    fontSize: "0.9rem"
  },
  input: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "0.9rem",
    boxSizing: "border-box"
  },
  smallButton: {
    padding: "0.25rem 0.5rem",
    fontSize: "0.8rem",
    backgroundColor: "#6c757d",
    color: "#fff",
    border: "none",
    borderRadius: "3px",
    cursor: "pointer"
  },
  dateInput: {
    padding: "0.25rem",
    border: "1px solid #ccc",
    borderRadius: "3px",
    marginLeft: "0.5rem"
  },
  checkboxContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem"
  },
  checkboxItem: {
    display: "flex",
    alignItems: "center",
    minWidth: "120px"
  },
  twoColumnSection: {
    display: "flex",
    gap: "1rem"
  },
  column: {
    flex: 1
  },
  optionsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem"
  },
  readOnly: {
    padding: "0.5rem",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    minHeight: "1rem"
  },
  formField: {
    marginBottom: "0.75rem"
  },
  fieldLabel: {
    fontWeight: "bold",
    marginBottom: "0.25rem",
    display: "block"
  },
  modalButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.5rem",
    marginTop: "1rem"
  },
  saveBtn: {
    backgroundColor: "#28a745",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  cancelBtn: {
    backgroundColor: "#4a4a4a",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  button: {
    backgroundColor: "#1D2C5F",
    border: "none",
    color: "#fff",
    padding: "0.4rem 0.75rem",
    borderRadius: "4px",
    cursor: "pointer"
  }
};

export { SOAPNewModal, SOAPViewModal };